/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * My profile › Memberships: live plans, visit cards, ended plans folded away.
 */
import React from "react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Skeleton,
} from "@lemonadetraining/web-ui";
import { IconChevronDown } from "@tabler/icons-react";
import { isTerminalSubscriptionStatus } from "@lemonadetraining/web-shared/utils/commitmentStatus";
import ActiveSubscriptionItem, { SubscriptionData } from "./ActiveSubscriptionItem";
import VisitCardDisplay from "../VisitCards/VisitCardDisplay";

interface MembershipsSectionProps {
  /** null while the first snapshot is on its way. */
  subscriptions: SubscriptionData[] | null;
  failed: boolean;
  onRetry: () => void;
}

const MembershipsSection: React.FC<MembershipsSectionProps> = ({
  subscriptions,
  failed,
  onRetry,
}) => {
  const ended = (subscriptions || []).filter((s) => isTerminalSubscriptionStatus(s.status));
  const current = (subscriptions || []).filter((s) => !isTerminalSubscriptionStatus(s.status));

  return (
    <>
      <Card data-testid="memberships-plans">
        <CardHeader>
          <CardTitle>Memberships</CardTitle>
          <CardDescription>What you pay your gym, and what it gives you.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {failed ? (
            <div role="alert" className="flex flex-wrap items-center gap-3 text-sm">
              <span className="text-destructive">We couldn&apos;t load your memberships.</span>
              <Button variant="outline" className="min-h-[44px]" onClick={onRetry}>
                Try again
              </Button>
            </div>
          ) : subscriptions === null ? (
            <div aria-busy="true" data-testid="memberships-loading">
              <span className="sr-only">Loading memberships</span>
              <Skeleton className="h-32 w-full rounded-lg" />
            </div>
          ) : current.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="memberships-empty">
              You don&apos;t have a membership right now. Your gym&apos;s page shows what it offers.
            </p>
          ) : (
            current.map((sub) => <ActiveSubscriptionItem subscription={sub} key={sub.id} />)
          )}

          {ended.length > 0 && (
            <Collapsible>
              <CollapsibleTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  className="group min-h-[44px] w-full justify-start gap-2 px-2"
                  data-testid="memberships-ended-toggle"
                >
                  <IconChevronDown
                    className="size-4 -rotate-90 transition-transform motion-reduce:transition-none group-data-[state=open]:rotate-0"
                    aria-hidden="true"
                  />
                  Ended plans ({ended.length})
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="flex flex-col gap-3 pt-2">
                {ended.map((sub) => (
                  <ActiveSubscriptionItem subscription={sub} key={sub.id} />
                ))}
              </CollapsibleContent>
            </Collapsible>
          )}
        </CardContent>
      </Card>

      <Card data-testid="memberships-visit-cards">
        <CardHeader>
          <CardTitle>Visit cards</CardTitle>
          <CardDescription>Visits left at each gym, and when they run out.</CardDescription>
        </CardHeader>
        <CardContent>
          <VisitCardDisplay />
        </CardContent>
      </Card>
    </>
  );
};

export default MembershipsSection;
